import { LabelsService } from '@services/labels/labels.service';
import { AuthService } from '@services/auth/auth.service';
import { HttpParams } from '@angular/common/http';
import { mergeMap, map, tap } from 'rxjs/operators';
import { PositionService } from '@services/position/position.service';
import { ApiService } from '@api/services/api/api.service';
import {
  QuotationBoundariesDTO,
  QuotationParamsDTO,
  QuotationDTO,
  CreditRequestDTO,
  CreditUseDTO,
  DomainItemDTO
} from '@core/modules/api/models';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { DomainService } from '../domain/domain.service';

@Injectable({
  providedIn: 'root'
})
export class QuotationService {
  /**
   * dati preventivo simulato
   */
  private simulatedQuotation$ = new BehaviorSubject<QuotationDTO>(null);

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private positionService: PositionService,
    private labelsService: LabelsService,
    private decimalPipe: DecimalPipe,
    private domainService: DomainService
  ) {}

  /**
   * restituisce i limiti per calcolare il preventivo
   */
  getSimulateBoundaries(): Observable<QuotationBoundariesDTO> {
    const params = this.enrichBoundariesParams(new HttpParams());

    return this.apiService.getQuotationsBoundaries({
      options: { params }
    });
  }

  /**
   * restituisce i limiti per calcolare il preventivo
   */
  getUpdateBoundaries(constraints?: {
    tanMin?: number;
    tanMax?: number;
  }): Observable<QuotationBoundariesDTO> {
    const positionId = this.positionService.getPositionId();
    const params = this.enrichBoundariesParams(new HttpParams(), constraints);

    return this.apiService.getPositionQuotationBoundaries({
      pathParams: { positionId },
      options: { params }
    });
  }

  /**
   * restituisce la lista di possibili durata da impostare per il pre ammortamento
   */
  getPreAmortizationOptions(
    quotation: QuotationDTO
  ): Observable<DomainItemDTO[]> {
    if (!quotation.financialTable) {
      return of([]);
    }

    return this.domainService
      .getDomain('BE_MESI_AMM', {
        filter: quotation.financialTable
      })
      .pipe(map(res => res.sort((a, b) => Number(b.key) - Number(a.key))));
  }

  /**
   * restituisce la lista dei giorni del mese in cui può avvenire il pagamento
   */
  getPaymentDaysOptions(quotation: QuotationDTO): Observable<DomainItemDTO[]> {
    if (!quotation.financialTable) {
      return of([]);
    }

    return this.domainService
      .getDomain('BE_PAYMENT_DAY', {
        filter: quotation.financialTable
      })
      .pipe(map(res => res.sort((a, b) => Number(a.key) - Number(b.key))));
  }

  /**
   * calcola e restituisce un nuovo preventivo
   */
  calcSimulatedQuotation(params: QuotationParamsDTO): Observable<QuotationDTO> {
    return this.getSimulation(params).pipe(
      tap(res => this.setSimulatedQuotation(res))
    );
  }

  /**
   * calcola il preventivo aggiornando e restituendo quello corrente
   */
  calcPositionQuotation(params: QuotationParamsDTO): Observable<QuotationDTO> {
    return this.updateQuotation(params).pipe(
      mergeMap(() => this.positionService.reloadPosition()),
      mergeMap(() => this.getPositionQuotation())
    );
  }

  /**
   * restituisce i dati del preventivo simulato
   */
  getSimulatedQuotation(): Observable<QuotationDTO> {
    return of(this.simulatedQuotation$.getValue());
  }

  /**
   * restituisce i dati del preventivo della pratica
   */
  getPositionQuotation(): Observable<QuotationDTO> {
    return of(this.positionService.getPosition().getQuotation());
  }

  /**
   * restituisce stream per osservare update del preventivo della position
   */
  getPositionQuotationWhenChange(): Observable<QuotationDTO> {
    return this.positionService
      .getPositionWhenChange({ whenExists: false })
      .pipe(map(position => position && position.getQuotation()));
  }

  /**
   * restituisce stream per osservare update del preventivo simulato
   */
  getSimulatedQuotationWhenChange(): Observable<QuotationDTO> {
    return this.simulatedQuotation$.asObservable();
  }

  /**
   * restituisce i dettagli del fido
   */
  getQuotationCreditRequest(positionId?: string): Observable<CreditRequestDTO> {
    const params = new HttpParams({
      fromObject: {
        positionId: positionId || this.positionService.getPositionId()
      }
    });

    return this.apiService
      .getQuotationCreditRequest({
        pathParams: { mode: 'revolving' },
        options: { params }
      })
      .pipe(
        map(res => ({
          ...res,
          usedPlafond:
            typeof res.usedPlafond === 'number'
              ? res.usedPlafond
              : res.accordedPlafond - res.availablePlafond
        }))
      );
  }

  /**
   * ritorna la lista di utilizzi
   */
  getQuotationCreditUses(
    positionId?: string
  ): Observable<{ active: CreditUseDTO; history: CreditUseDTO[] }> {
    const params = new HttpParams({
      fromObject: {
        positionId: positionId || this.positionService.getPositionId()
      }
    });

    return this.apiService
      .getQuotationCreditUse({
        pathParams: { mode: 'revolving' },
        options: { params }
      })
      .pipe(
        map(res => ({
          active: res.uses.find(x => x.status === '40'),
          history: res.uses.filter(x => x.status !== '40')
        }))
      );
  }

  /**
   * restituisce il product code impostato
   */
  getProductCode(): string {
    return this.authService.getAuthUserAttributes()['product-code'];
  }

  /**
   * restituisce il process type impostato
   */
  getProcessType(): string {
    const position = this.positionService.getPosition();

    if (position && position.isPredeliberate()) {
      return '02';
    } else {
      return '01';
    }
  }

  /**
   * restituisce codice campagna
   */
  getCampaignCode(): string {
    const position = this.positionService.getPosition();

    return position && position.getCampaignCode();
  }

  /**
   * restituisce i label con all'interno i dati del preventivo
   */
  getLabelWithQuotationData(
    labelKey: string,
    quotation: QuotationDTO,
    extraData?: {
      paymentDays?: string[];
    }
  ): string {
    // lista di campi che devono essere formattati come importi (mantenere allinetati con DTO)
    const amountKeys = [
      'montante',
      'financedAmount',
      'dueAmount',
      'disbursedAmount',
      'netDisbursedAmountToCustomer',
      'totalCostOfCredit',
      'installment',
      'requestedAmount',
      'singleCommunicationCost',
      'totalCommunicationCost',
      'managementFees',
      'guaranteeFee',
      'installmentFee',
      'totalInstallmentFee',
      'singleBankReportingStamp',
      'totalBankReportingStamp',
      'netDisbursedAmountObligations'
    ];
    // lista di campi che devono essere formattati come numeri (mantenere allinetati con DTO)
    const decimalKeys = [
      'duration',
      'totalDuration',
      'durationInYears',
      'taeg',
      'tan',
      'teg'
    ];

    const params = Object.keys(quotation).reduce((obj, key) => {
      if (amountKeys.includes(key)) {
        obj[key] = this.decimalPipe.transform(quotation[key], '1.2-2');
      } else if (decimalKeys.includes(key)) {
        obj[key] = this.decimalPipe.transform(quotation[key], '1.0-2');
      } else {
        obj[key] = quotation[key];
      }

      return obj;
    }, {});

    if (extraData && extraData.paymentDays) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      params['paymentDays'] = extraData.paymentDays.join(', ');
    }

    return this.labelsService.getLabel(labelKey, {
      interpolateParams: params
    });
  }

  /**
   * restituisce la cpi disponibile
   */
  getAvailableCPI(): Observable<string> {
    return this.domainService
      .getDomain('BE_CPI', {
        params: { positionId: this.positionService.getPositionId() }
      })
      .pipe(map(list => (list[0] ? list[0].key : null)));
  }

  /**
   * restituisce codice errore per preventivo oltre i limiti
   */
  getOutOfLimitsErroCode(): string {
    return 'PRI0006';
  }

  /**
   * restituisce la simulazione del preventivo
   */
  private getSimulation(params: QuotationParamsDTO): Observable<QuotationDTO> {
    return this.apiService.postQuotations({
      body: this.enrichQuotationParams({ ...params })
    });
  }

  /**
   * aggiorna il preventivo
   */
  private updateQuotation(params: QuotationParamsDTO): Observable<void> {
    const positionId = this.positionService.getPositionId();

    return this.apiService.putPositionQuotation({
      pathParams: { positionId },
      body: this.enrichQuotationParams({ ...params })
    });
  }

  /**
   * arricchisie i parametri da passare per il calcolo del prevetivo
   */
  private enrichQuotationParams(
    params: QuotationParamsDTO
  ): QuotationParamsDTO {
    params.fiscalCode = this.getFiscalCode();

    if (this.getProductCode()) {
      params.productCode = this.getProductCode();
    }

    if (this.getProcessType()) {
      params.processType = this.getProcessType();
    }

    if (this.getCampaignCode()) {
      params.campaignCode = this.getCampaignCode();
    }

    return params;
  }

  private enrichBoundariesParams(
    params: HttpParams,
    constraints?: {
      tanMin?: number;
      tanMax?: number;
    }
  ): HttpParams {
    params = params.set('fiscalCode', this.getFiscalCode());

    if (this.getProductCode()) {
      params = params.set('productCode', this.getProductCode());
    }

    if (this.getProcessType()) {
      params = params.set('processType', this.getProcessType());
    }

    if (this.getCampaignCode()) {
      params = params.set('campaignCode', this.getCampaignCode());
    }

    if (constraints) {
      Object.keys(constraints).forEach(
        key => (params = params.set(key, constraints[key]))
      );
    }

    return params;
  }

  /**
   * aggiorna preventivo commerciale
   */
  private setSimulatedQuotation(quotation: QuotationDTO): void {
    if (quotation) {
      this.simulatedQuotation$.next({ ...quotation });
    } else {
      this.simulatedQuotation$.next(null);
    }
  }

  /**
   * restitusice il codice fiscale
   */
  private getFiscalCode(): string {
    return this.positionService.getPosition()?.getCustomer()?.taxNumber;
  }
}
